import React, { useState, useEffect, useMemo } from 'react';
import Modal from '../../../Components/Modal';
import 'react-datepicker/dist/react-datepicker.css';
import '../../datepicker.css';
import './index.scss';
import PurchaseService from '../../../Data/PurchaseService';
import { logBehavior } from '../../../Data/Behavior';
import { Product as ProductType } from 'ai-api/types/purchase';
import AdminService from '../../../Data/Admin';

import {
    Header as ProductHeader,
    LoadingSpinner,
    ErrorSnackbar,
    ProductDetails,
    PurchaseButton,
    PurchaseConfirmModal,
    PaymentSection,
} from '../../../Components/Product';
import { Teacher } from '../../../Components/Product/TeacherCard';
import { useSession } from '@/Recoil/Session/Session';
import { tLoader } from '@/Utils/i18n';
import { languageManager } from '@/Utils/languageManager';

const translations = {
    en: {
        "Product not found": "Product not found",
        "Error loading product. Please try again.": "Error loading product. Please try again.",
        "Please select a teacher": "Please select a teacher",
        "Please select a date and time": "Please select a date and time",
        "Product information is missing": "Product information is missing",
        "Payment processing failed. Please try again.": "Payment processing failed. Please try again.",
        "Loading product information...": "Loading product information...",
        "Preparing payment page...": "Preparing payment page...",
        "Privacy Policy": "Privacy Policy",
        "Terms of Use": "Terms of Use"
    },
    id: {
        "Product not found": "Produk tidak ditemukan",
        "Error loading product. Please try again.": "Kesalahan memuat produk. Silakan coba lagi.",
        "Please select a teacher": "Silakan pilih guru",
        "Please select a date and time": "Silakan pilih tanggal dan waktu",
        "Product information is missing": "Informasi produk tidak lengkap",
        "Payment processing failed. Please try again.": "Pemrosesan pembayaran gagal. Silakan coba lagi.",
        "Loading product information...": "Memuat informasi produk...",
        "Preparing payment page...": "Menyiapkan halaman pembayaran...",
        "Privacy Policy": "Kebijakan Privasi",
        "Terms of Use": "Ketentuan Penggunaan"
    },
    ko: {
        "Product not found": "제품을 찾을 수 없습니다",
        "Error loading product. Please try again.": "제품 로드 중 오류가 발생했습니다. 다시 시도해주세요.",
        "Please select a teacher": "선생님을 선택해주세요",
        "Please select a date and time": "날짜와 시간을 선택해주세요",
        "Product information is missing": "제품 정보가 없습니다",
        "Payment processing failed. Please try again.": "결제 처리에 실패했습니다. 다시 시도해주세요.",
        "Loading product information...": "제품 정보를 불러오는 중...",
        "Preparing payment page...": "결제 페이지 준비 중...",
        "Privacy Policy": "개인정보 보호정책",
        "Terms of Use": "이용약관"
    },
    vn: {
        "Product not found": "Không tìm thấy sản phẩm",
        "Error loading product. Please try again.": "Lỗi khi tải sản phẩm. Vui lòng thử lại.",
        "Please select a teacher": "Vui lòng chọn giáo viên",
        "Please select a date and time": "Vui lòng chọn ngày và giờ",
        "Product information is missing": "Thông tin sản phẩm bị thiếu",
        "Payment processing failed. Please try again.": "Xử lý thanh toán không thành công. Vui lòng thử lại.",
        "Loading product information...": "Đang tải thông tin sản phẩm...",
        "Preparing payment page...": "Đang chuẩn bị trang thanh toán...",
        "Privacy Policy": "Chính sách bảo mật",
        "Terms of Use": "Điều khoản sử dụng"
    }
};

interface ProductPageProps {
    productId: string;
}

function ProductPage({ productId }: ProductPageProps) {
    const { session } = useSession();
    const [, forceUpdate] = useState({});
    const t = tLoader(translations);

    const [product, setProduct] = useState<ProductType | null>(null);
    const [loading, setLoading] = useState<boolean>(true);
    const [showPurchaseConfirm, setShowPurchaseConfirm] = useState(false);
    const [selectedDate, setSelectedDate] = useState<Date | null>(null);
    const [selectedTime, setSelectedTime] = useState<Date | null>(null);
    const [isPurchasing, setIsPurchasing] = useState(false);
    const [showErrorSnackbar, setShowErrorSnackbar] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');

    useEffect(() => {
        const unsubscribe = languageManager.subscribe(() => forceUpdate({}));
        return unsubscribe;
    }, []);

    useEffect(() => {
        let timer: ReturnType<typeof setTimeout> | undefined = undefined;
        if (errorMessage) {
            setShowErrorSnackbar(true);

            // Hide error message after 5 seconds
            timer = setTimeout(() => {
                setShowErrorSnackbar(false);
            }, 5000);
        }
        return () => {
            clearTimeout(timer);
        };
    }, [errorMessage]);

    // Payment related states
    const [showPaymentSection, setShowPaymentSection] = useState(false);
    const [clientSecret, setClientSecret] = useState<string | null>(null);

    // Section expand/collapse states
    const [descriptionExpanded, setDescriptionExpanded] = useState(false);
    const [teacherExpanded, setTeacherExpanded] = useState(false);
    const [calendarExpanded, setCalendarExpanded] = useState(false);

    // Fetch product data when productId changes
    useEffect(() => {
        const fetchProductData = async () => {
            setLoading(true);
            
            try {
                // Call the API to get product details
                const response = await PurchaseService.getProduct('korean_session.250509.01');
                
                if (response.code === 200 && response.data) {
                    setProduct(response.data);
                    // Log product view behavior
                    logBehavior('product_view', {
                        productId,
                        productName: response.data.name,
                        productPrice: response.data.finalAmount,
                        currency: 'IDR'
                    });
                } else {
                    setErrorMessage(t('Product not found'));
                }
            } catch (err) {
                console.error('Error fetching product:', err);

                // Don't use mock data, just show the error
                setErrorMessage(t('Error loading product. Please try again.'));

                // Still log the view attempt for analytics
                logBehavior('product_view_error', {
                    productId,
                    error: (err as Error).message || 'Unknown error'
                });
            } finally {
                setLoading(false);
            }
        };
        
        // Fetch product data
        fetchProductData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [productId]);
    
    // Teacher data
    const teachers: Teacher[] = useMemo(()=>[
        {
            id: 1,
            name: 'Heesoon Kim',
            university: '한국외국어대학교 대학원',
            major: 'Global Korean',
            languages: ['Korean', 'English', 'Bahasa Indonesian'],
            image: 'https://static.koreanmate.app/pnGvtnFkyTzvHCWUDQjobfrEyfVjMIdz/heesoon.png',
            rating: 4.9,
            reviews: 93,
            expertise: ['Korean', 'Indonesian']
        }
    ], []);
    
    const [selectedTeacher, setSelectedTeacher] = useState<Teacher | null>(null);

    // Update selectedTeacher when teachers are loaded
    useEffect(() => {
        if (teachers.length > 0 && !selectedTeacher) {
            setSelectedTeacher(teachers[0]);
        }
    }, [teachers, selectedTeacher]);
    
    const handlePurchase = () => {
        if (false) {
            const currentUrl = window.location.pathname + window.location.search;
            window.location.href = `/login?redirectTo=${encodeURIComponent(currentUrl)}`;
            return;
        }
        
        if (!selectedTeacher) {
            setErrorMessage(t('Please select a teacher'));
            return;
        }

        if (!selectedDate || !selectedTime) {
            setErrorMessage(t('Please select a date and time'));
            return;
        }
        
        // Log the purchase attempt
        logBehavior('purchase_attempt', {
            productId: product?.productid,
            teacherId: selectedTeacher.id,
            sessionDate: selectedDate?.toISOString(),
            sessionTime: selectedTime?.toISOString()
        });
        
        setShowPurchaseConfirm(true);
    };
    
    
    const handleConfirmPurchase = async () => {
        if (!product) {
            setErrorMessage(t('Product information is missing'));
            return;
        }
        
        try {
            // Log purchase confirmation
            logBehavior('purchase_confirm', {
                productId: product.productid,
                teacherId: selectedTeacher?.id,
                sessionDate: selectedDate?.toISOString(),
                sessionTime: selectedTime?.toISOString(),
                amount: product.finalAmount,
                currency: 'IDR'
            });
            
            // Close the purchase confirmation modal
            setShowPurchaseConfirm(false);
            
            // Show loading indicator
            setIsPurchasing(true);
            
            // Store the session info in sessionStorage for the payment page
            sessionStorage.setItem('payment_info', JSON.stringify({
                productId: product.productid,
                productName: product.name,
                productPrice: product.finalAmount,
                currency: 'IDR',
                sessionDate: selectedDate ? selectedDate.toISOString() : null,
                sessionTime: selectedTime ? selectedTime.toISOString() : null,
                teacherName: selectedTeacher?.name
            }));
            
            // Create a Stripe payment intent using the API
            try {
                const response = await PurchaseService.createStripeIntent(
                    product.productid,
                    session?.email,
                    session?.name
                );
                
                if (response.code === 200 && response.data && response.data.clientSecret) {
                    // Set the client secret and show the payment section
                    setClientSecret(response.data.clientSecret);
                    setShowPaymentSection(true);
                    setIsPurchasing(false); // Hide the loading indicator

                    const rootElement = document.querySelector('#root');
                    if (rootElement) {
                        setTimeout(()=>{
                            rootElement.scrollTo({top: rootElement.getBoundingClientRect().height, behavior: 'smooth'});
                        },50); // Scroll to the bottom of the page
                    }
                } else {
                    throw new Error('Invalid response from payment intent creation');
                }
            } catch (intentError) {
                console.error('Error creating payment intent:', intentError);
                setIsPurchasing(false);
                setErrorMessage(t('Payment processing failed. Please try again.'));
            }

        } catch (error) {
            console.error('Error in purchase process:', error);
            setIsPurchasing(false);
            setErrorMessage(t('Payment processing failed. Please try again.'));
        }
    };

    // Handle payment success
    const handlePaymentSuccess = async () => {
        try {
            // Send purchase notification email
            if (product && session) {
                await AdminService.sendPurchaseEmail(
                    product,
                    selectedTeacher?.name || null,
                    selectedDate,
                    selectedTime,
                    session.email,
                    session.name
                );
                console.log('Purchase notification email sent successfully');
            }
        } catch (error) {
            console.error('Failed to send purchase notification email:', error);
            // Continue with redirect even if email fails
        } finally {
            // Clear the payment info from sessionStorage
            sessionStorage.removeItem('payment_info');
            // Redirect to homepage or thank you page
            window.location.href = '/purchase/history';
        }
    };

    // Handle payment cancellation
    const handlePaymentCancel = () => {
        // Hide the payment section and show product details again
        setShowPaymentSection(false);
        setClientSecret(null);
    };

    if (loading) {
        return (
            <div className="product-page">
                <ProductHeader />
                <LoadingSpinner message={t("Loading product information...")} />
            </div>
        );
    }

    return (
        <div className="product-page">
            
            <div>
                {/* Product Details Section */}
                <ProductDetails
                    product={product}
                    descriptionExpanded={descriptionExpanded}
                    setDescriptionExpanded={setDescriptionExpanded}
                    teacherExpanded={teacherExpanded}
                    setTeacherExpanded={setTeacherExpanded}
                    calendarExpanded={calendarExpanded}
                    setCalendarExpanded={setCalendarExpanded}
                    teachers={teachers}
                    selectedTeacher={selectedTeacher}
                    setSelectedTeacher={setSelectedTeacher}
                    selectedDate={selectedDate}
                    setSelectedDate={setSelectedDate}
                    selectedTime={selectedTime}
                    setSelectedTime={setSelectedTime}
                    disabled={showPaymentSection}
                />

                {/* Payment Section - Shown when showPaymentSection is true */}
                {showPaymentSection && clientSecret && (
                    <PaymentSection
                        product={product}
                        selectedTeacher={selectedTeacher}
                        selectedDate={selectedDate}
                        selectedTime={selectedTime}
                        clientSecret={clientSecret}
                        onSuccess={handlePaymentSuccess}
                        onCancel={handlePaymentCancel}
                    />
                )}

                {/* Purchase Button - Hidden when payment section is showing */}
                {!showPaymentSection && (
                    <PurchaseButton
                        selectedTeacher={selectedTeacher}
                        selectedDate={selectedDate}
                        selectedTime={selectedTime}
                        onClick={handlePurchase}
                    />
                )}
            </div>
            
            {/* Purchase Confirmation Modal */}
            <PurchaseConfirmModal
                isOpen={showPurchaseConfirm}
                onClose={() => setShowPurchaseConfirm(false)}
                product={product}
                selectedDate={selectedDate}
                selectedTime={selectedTime}
                onConfirm={handleConfirmPurchase}
            />
            
            {/* Loading Modal */}
            <Modal isOpen={isPurchasing} onClose={()=>{}}>
                <LoadingSpinner message={t("Preparing payment page...")} />
            </Modal>

            {/* Error Snackbar */}
            <ErrorSnackbar
                show={showErrorSnackbar}
                message={errorMessage}
            />
            <footer className="Footer">
                <p>{`© ${new Date().getFullYear()} KOREAN CULTURE MATE. All rights reserved.`}</p>
                <div style={{display: 'flex', gap: '1rem', flexDirection: 'row'}}>
                    <a href={"/privacypolicy"}><p>{t("Privacy Policy")}</p></a>
                    <a href={"/termsofuse"}><p>{t("Terms of Use")}</p></a>
                </div>
                <a href={"mailto:help@koreanmate.app"} target={"_blank"} rel={"noreferrer"}><p>{"📧help@koreanmate.app"}</p></a>
            </footer>
        </div>
    );
}

export default ProductPage;